import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Input, Label } from 'reactstrap';
import { Tabs, Tab, Slider } from '@mui/material';
import { getEmailSettings } from '../../store/Integration';
import './EmailSettingsModal.css';

const DEFAULT_SETTINGS = {
    warmup: { enabled: false, paused: false, increasePerDay: 2, replyRate: 30, dailyLimit: 40, readEmulation: true, warmupTag: 'warmup', weekdaysOnly: false, warmCustomTrackingDomain: false, openRate: 100, spamProtection: 100, markImportant: 100 },
    campaign: { dailySendingLimit: 50 },
    stats: { sent: 0, received: 0, savedFromSpam: 0, inboxRate: 0, openRate: 0, replyRate: 0, lastUpdated: null }
};

// Toggle Button Group Component
const ToggleButtonGroup = ({ value, onChange, labels = ['Disable', 'Enable'] }) => (
    <div className="esm-toggle-btn-group">
        <button type="button" onClick={() => onChange(false)} className={`esm-toggle-btn ${!value ? 'active' : ''}`}>{labels[0]}</button>
        <button type="button" onClick={() => onChange(true)} className={`esm-toggle-btn ${value ? 'active' : ''}`}>{labels[1]}</button>
    </div>
);

const EmailSettingsModal = ({ isOpen, toggle, integration }) => {
    const dispatch = useDispatch();
    const { emailSettings } = useSelector(state => state['Integration']);
    const [activeTab, setActiveTab] = useState(0);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [saving, setSaving] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Load settings when panel opens
    useEffect(() => {
        if (isOpen && integration?.['_id']) dispatch(getEmailSettings(integration['_id']));
    }, [isOpen, integration?.['_id'], dispatch]);

    // Populate settings from Redux
    useEffect(() => {
        if (emailSettings?.['data']?.[integration?.['_id']]) {
            const stored = emailSettings['data'][integration['_id']];
            setSettings({
                warmup: { ...DEFAULT_SETTINGS['warmup'], ...(stored['warmup'] || {}) },
                campaign: { ...DEFAULT_SETTINGS['campaign'], ...(stored['campaign'] || {}) },
                stats: { ...DEFAULT_SETTINGS['stats'], ...(stored['stats'] || {}) }
            });
        }
    }, [emailSettings, integration?.['_id']]);

    const updateWarmup = (key, value) => setSettings(prev => ({ ...prev, warmup: { ...prev['warmup'], [key]: value } }));
    const updateCampaign = (key, value) => setSettings(prev => ({ ...prev, campaign: { ...prev['campaign'], [key]: value } }));

    const handleSave = () => {
        setSaving(true);
        dispatch({
            type: 'UPDATE_EMAIL_SETTINGS',
            payload: { integrationId: integration['_id'], settings: { warmup: settings['warmup'], campaign: settings['campaign'] }, onSuccess: () => { setSaving(false); toggle(); } }
        });
        setTimeout(() => setSaving(false), 3000);
    };

    // Warmup control handlers
    const handleEnableWarmup = () => updateWarmup('enabled', true);
    const handleDisableWarmup = () => updateWarmup('enabled', false);
    const handlePauseWarmup = () => updateWarmup('paused', !settings['warmup']['paused']);

    const accountEmail = integration?.['tokens']?.['email'] || integration?.['accountIdentifier'] || integration?.['name'] || 'Email Account';

    // Get warmup status text
    const getWarmupStatus = () => {
        if (!settings['warmup']['enabled']) return { text: 'Disabled', color: 'disabled' };
        if (settings['warmup']['paused']) return { text: 'Paused', color: 'paused' };
        return { text: 'Active', color: 'active' };
    };
    const warmupStatus = getWarmupStatus();

    if (!isOpen) return null;

    return (
        <div className="email-settings-modal">
            {/* Backdrop */}
            <div className="esm-backdrop" onClick={toggle}></div>

            {/* Slider Panel */}
            <div className={`esm-slider-panel ${isOpen ? 'open' : ''}`}>
                {/* Header */}
                <div className="esm-panel-header">
                    <div className="esm-header-content">
                        <i className="fa fa-envelope"></i>
                        <span>{accountEmail}</span>
                    </div>
                    <button className="esm-close-btn" onClick={toggle}>
                        <i className="fa fa-times"></i>
                    </button>
                </div>

                {/* Stats Section - Always visible */}
                <div className="esm-stats-section">
                    <div className="esm-stats-row">
                        <div className="esm-stat-item">
                            <span className="esm-stat-number">{settings['stats']['sent']}</span>
                            <span className="esm-stat-text">Emails Sent</span>
                        </div>
                        <div className="esm-stat-item">
                            <span className="esm-stat-number">{settings['stats']['received']}</span>
                            <span className="esm-stat-text">Emails Received</span>
                        </div>
                        <div className="esm-stat-item">
                            <span className="esm-stat-number">{settings['stats']['savedFromSpam']}</span>
                            <span className="esm-stat-text">Saved from Spam</span>
                        </div>
                    </div>
                </div>

                {/* Warmup Status & Controls */}
                <div className="esm-warmup-controls">
                    <div className="esm-warmup-status">
                        <span className="esm-status-label">Warmup Status:</span>
                        <span className={`esm-status-badge ${warmupStatus['color']}`}>{warmupStatus['text']}</span>
                    </div>
                    <div className="esm-warmup-buttons">
                        {!settings['warmup']['enabled'] ? (
                            <button className="esm-control-btn enable" onClick={handleEnableWarmup}>
                                <i className="fa fa-play"></i> Enable
                            </button>
                        ) : (
                            <>
                                <button className="esm-control-btn pause" onClick={handlePauseWarmup}>
                                    <i className={`fa fa-${settings['warmup']['paused'] ? 'play' : 'pause'}`}></i>
                                    {settings['warmup']['paused'] ? 'Resume' : 'Pause'}
                                </button>
                                <button className="esm-control-btn disable" onClick={handleDisableWarmup}>
                                    <i className="fa fa-stop"></i> Disable
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} className="esm-tabs">
                    <Tab label="Warmup" />
                    <Tab label="Settings" />
                </Tabs>

                {/* Panel Content */}
                <div className="esm-panel-content">
                    {/* Warmup Tab */}
                    {activeTab === 0 && (
                        <div className="esm-tab-content">
                            {/* Basic Warmup Settings Section */}
                            <div className="esm-basic-section">
                                <div className="esm-section-header">
                                    <i className="fa fa-sliders"></i>
                                    <span>Warmup Configuration</span>
                                </div>

                                {/* Warmup Settings - Show regardless of enabled state */}
                                <div className="esm-slider-grid">
                                    <div className="esm-slider-item">
                                        <Label className="esm-slider-label">Increase per day: {settings['warmup']['increasePerDay']}</Label>
                                        <Slider value={settings['warmup']['increasePerDay']} onChange={(e, v) => updateWarmup('increasePerDay', v)} min={1} max={10} valueLabelDisplay="auto" />
                                    </div>
                                    <div className="esm-slider-item">
                                        <Label className="esm-slider-label">Reply rate: {settings['warmup']['replyRate']}%</Label>
                                        <Slider value={settings['warmup']['replyRate']} onChange={(e, v) => updateWarmup('replyRate', v)} min={10} max={100} valueLabelDisplay="auto" />
                                    </div>
                                    <div className="esm-slider-item">
                                        <Label className="esm-slider-label">Daily warmup limit: {settings['warmup']['dailyLimit']}</Label>
                                        <Slider value={settings['warmup']['dailyLimit']} onChange={(e, v) => updateWarmup('dailyLimit', v)} min={5} max={100} valueLabelDisplay="auto" />
                                    </div>
                                </div>

                                <div className="esm-toggle-row">
                                    <div className="esm-toggle-info">
                                        <Label className="esm-label-sm">Read emulation</Label>
                                        <p className="esm-description-sm">Simulate reading emails to improve engagement metrics</p>
                                    </div>
                                    <ToggleButtonGroup value={settings['warmup']['readEmulation']} onChange={(v) => updateWarmup('readEmulation', v)} />
                                </div>

                                <div className="esm-input-group">
                                    <Label className="esm-label-sm">Warmup tag (for filtering)</Label>
                                    <Input type="text" value={settings['warmup']['warmupTag']} onChange={(e) => updateWarmup('warmupTag', e.target.value)} className="esm-input" placeholder="warmup" />
                                </div>
                            </div>

                            {/* Advanced Settings Toggle Button */}
                            <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="esm-advanced-toggle">
                                {showAdvanced ? 'Hide' : 'Show'} advanced settings
                                <i className={`fa fa-chevron-${showAdvanced ? 'up' : 'down'}`}></i>
                            </button>

                            {/* Advanced Settings Section */}
                            {showAdvanced && (
                                <div className="esm-advanced-section">
                                    <div className="esm-advanced-header">
                                        <i className="fa fa-fire"></i>
                                        <span>Warmup Settings Advanced</span>
                                    </div>
                                    <div className="esm-divider"></div>

                                    {/* Weekdays only */}
                                    <div className="esm-advanced-row">
                                        <div className="esm-advanced-info">
                                            <Label className="esm-advanced-label">Weekdays only</Label>
                                            <p className="esm-advanced-desc">Only send warmup emails on weekdays for a more natural sending pattern</p>
                                        </div>
                                        <ToggleButtonGroup value={settings['warmup']['weekdaysOnly']} onChange={(v) => updateWarmup('weekdaysOnly', v)} />
                                    </div>

                                    {/* Read emulation */}
                                    <div className="esm-advanced-row">
                                        <div className="esm-advanced-info">
                                            <Label className="esm-advanced-label">Read emulation</Label>
                                            <p className="esm-advanced-desc">Spend time and scroll through your warmup email to emulate human-like reading</p>
                                        </div>
                                        <ToggleButtonGroup value={settings['warmup']['readEmulation']} onChange={(v) => updateWarmup('readEmulation', v)} />
                                    </div>

                                    {/* Warm custom tracking domain */}
                                    <div className="esm-advanced-row">
                                        <div className="esm-advanced-info">
                                            <Label className="esm-advanced-label">Warm custom tracking domain</Label>
                                            <p className="esm-advanced-desc">Include your custom tracking domain in your warmup emails to further improve deliverability</p>
                                        </div>
                                        <ToggleButtonGroup value={settings['warmup']['warmCustomTrackingDomain']} onChange={(v) => updateWarmup('warmCustomTrackingDomain', v)} />
                                    </div>

                                    {/* Open Rate Slider */}
                                    <div className="esm-advanced-row esm-advanced-slider-row">
                                        <div className="esm-advanced-info">
                                            <Label className="esm-advanced-label">Open Rate</Label>
                                            <p className="esm-advanced-desc">How many of your warm up emails to open</p>
                                        </div>
                                        <div className="esm-advanced-slider-wrapper">
                                            <Slider value={settings['warmup']['openRate']} onChange={(e, v) => updateWarmup('openRate', v)} min={0} max={100} valueLabelDisplay="auto" />
                                            <span className="esm-slider-value">{settings['warmup']['openRate']}</span>
                                        </div>
                                    </div>

                                    {/* Spam Protection Slider */}
                                    <div className="esm-advanced-row esm-advanced-slider-row">
                                        <div className="esm-advanced-info">
                                            <Label className="esm-advanced-label">Spam Protection</Label>
                                            <p className="esm-advanced-desc">How many of your warm up emails to save from spam folder</p>
                                        </div>
                                        <div className="esm-advanced-slider-wrapper">
                                            <Slider value={settings['warmup']['spamProtection']} onChange={(e, v) => updateWarmup('spamProtection', v)} min={0} max={100} valueLabelDisplay="auto" />
                                            <span className="esm-slider-value">{settings['warmup']['spamProtection']}</span>
                                        </div>
                                    </div>

                                    {/* Mark Important Slider */}
                                    <div className="esm-advanced-row esm-advanced-slider-row">
                                        <div className="esm-advanced-info">
                                            <Label className="esm-advanced-label">Mark Important</Label>
                                            <p className="esm-advanced-desc">How many of your warm up emails to mark as important</p>
                                        </div>
                                        <div className="esm-advanced-slider-wrapper">
                                            <Slider value={settings['warmup']['markImportant']} onChange={(e, v) => updateWarmup('markImportant', v)} min={0} max={100} valueLabelDisplay="auto" />
                                            <span className="esm-slider-value">{settings['warmup']['markImportant']}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Settings Tab */}
                    {activeTab === 1 && (
                        <div className="esm-tab-content">
                            <div className="esm-slider-item">
                                <Label className="esm-slider-label">Daily sending limit: {settings['campaign']['dailySendingLimit']}</Label>
                                <Slider value={settings['campaign']['dailySendingLimit']} onChange={(e, v) => updateCampaign('dailySendingLimit', v)} min={1} max={500} valueLabelDisplay="auto" />
                                <p className="esm-description-sm">Maximum number of emails to send per day from this account</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="esm-panel-footer">
                    <button className="btn-style-new" onClick={handleSave} disabled={saving}>
                        {saving ? <><i className="fa fa-spinner fa-spin"></i> Saving...</> : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmailSettingsModal;
